import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  computeFailedPayments,
  computeMetrics,
  computeOverdueInvoices,
  computeRecentActivity,
  isPaidOrSetUp,
  paymentMethodOf,
  watchCustomers,
  watchEmailLog,
  watchInvoices,
  watchSettings,
  watchSmsLog,
} from '../lib/data.js'
import { fmtDateTime, fmtMoney, statusPillClass } from '../lib/format.js'
import { currentCycleInvoice } from '../lib/paymentBadge.js'

export default function Dashboard() {
  const [customers, setCustomers] = useState({})
  const [invoices, setInvoices] = useState({})
  const [settings, setSettings] = useState({})
  const [emailLog, setEmailLog] = useState([])
  const [smsLog, setSmsLog] = useState([])
  useEffect(() => watchCustomers(setCustomers), [])
  useEffect(() => watchInvoices(setInvoices), [])
  useEffect(() => watchSettings((s) => setSettings(s || {})), [])
  useEffect(() => watchEmailLog(setEmailLog, 50), [])
  useEffect(() => watchSmsLog(setSmsLog, 500), [])

  // Texts sent this calendar month. /smsLog rows are written by the
  // sendSmsGateway + sendSmsGatewayBulk Cloud Functions on every send
  // attempt; we count only status === 'sent' so failed attempts don't
  // inflate the headline number.
  const smsThisMonth = (() => {
    const now = new Date()
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
    return smsLog.filter((r) => r.status === 'sent' && (r.sentAt || 0) >= monthStart).length
  })()

  // Resend health — show a banner if the API key is missing or rejected.
  const emailKeySaved = !!(settings.resendApiKey || '').trim()
  const emailBroken = emailKeySaved && settings.emailHealth?.status === 'error'
  const emailMissing = !emailKeySaved

  const m = computeMetrics(customers, invoices)
  const recent = computeRecentActivity(invoices, 12)
  // CURRENT billing-cycle invoice per customer — feeds the setup/paid pill, and
  // resets each cycle so a prior month's paid invoice stops reading as paid.
  const invByCustomer = {}
  for (const inv of Object.values(invoices || {})) {
    if (!inv?.customerId) continue
    ;(invByCustomer[inv.customerId] = invByCustomer[inv.customerId] || []).push(inv)
  }
  const currentInvoiceByCustomer = {}
  for (const [cid, list] of Object.entries(invByCustomer)) {
    currentInvoiceByCustomer[cid] = currentCycleInvoice(customers[cid], list) || { status: 'unpaid' }
  }
  const failedList = computeFailedPayments(invoices, customers)
  const overdueList = computeOverdueInvoices(invoices, customers)
  const failedAmount = failedList.reduce((s, r) => s + (Number(r.amount) || 0), 0)
  const overdueAmount = overdueList.reduce((s, r) => s + (Number(r.amount) || 0), 0)

  // Recent signups — customers whose bank-connect flow completed in the
  // last 7 days. activatedAt is written by activateSignupCustomer() in
  // the public signup flow, so we get a clean window of "just signed up".
  const SEVEN_DAYS = 7 * 24 * 3600 * 1000
  const now = Date.now()
  const recentSignups = Object.entries(customers || {})
    .map(([id, v]) => ({ id, ...v }))
    .filter(c => c.activatedAt && (now - Number(c.activatedAt)) < SEVEN_DAYS && c.stripePaymentMethodId)
    .sort((a, b) => Number(b.activatedAt) - Number(a.activatedAt))

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-extrabold">Dashboard</h1>

      {/* Email (Resend) health banner — only shows when emails are actually
          broken or never set up. */}
      {(emailBroken || emailMissing) && (
        <Link
          to="/settings"
          className={`block rounded-lg border p-3 hover:shadow ${
            emailBroken
              ? 'border-red-300 bg-red-50 text-red-800'
              : 'border-amber-300 bg-amber-50 text-amber-900'
          }`}>
          <div className="font-bold text-sm">
            {emailBroken
              ? '⚠️ Resend API key rejected — emails are not sending'
              : '⚠️ Resend API key missing — emails will not send'}
          </div>
          <div className="text-xs mt-1 opacity-90">
            {emailBroken
              ? 'The saved Resend API key was rejected. Generate a new one at resend.com/api-keys and paste it in Settings.'
              : 'Paste your Resend API key in Settings so the app can send invoice and signup emails.'}
            {' '}Click here to fix →
          </div>
        </Link>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <Stat label="Active customers" value={m.activeCustomers} hint={`${Object.keys(customers).length} total`} />
        <Stat label="Monthly recurring" value={fmtMoney(m.monthlyRevenue)} accent />
        <Stat label="Invoices due" value={m.dueThisCycleCount} hint={fmtMoney(m.dueThisCycleAmount)} />
        <Link to="/failed-payments" className="contents">
          <Stat
            label="Failed Payments"
            value={failedList.length}
            hint={failedList.length > 0 ? fmtMoney(failedAmount) : 'All caught up'}
            tone={failedList.length > 0 ? 'red' : 'green'}
          />
        </Link>
        <Link to="/overdue" className="contents">
          <Stat
            label="Overdue"
            value={overdueList.length}
            hint={overdueList.length > 0 ? fmtMoney(overdueAmount) : 'None past due'}
            tone={overdueList.length > 0 ? 'red' : 'green'}
          />
        </Link>
        <Stat
          label="Texts sent this month"
          value={smsThisMonth}
          hint={smsThisMonth === 0 ? 'No SMS sent yet' : 'Gateway sends to /smsLog'}
        />
      </div>

      {/* Recent signups — surfaces customers who just completed the
          self-signup flow so Mark can welcome them or sanity-check the
          bank link. Only shown when there's something to show; the
          green check + "Auto-Draft Active" label echoes the badge on
          the Customers page so the visual language matches. */}
      {recentSignups.length > 0 && (
        <div className="card border-brand-green-deep border-2">
          <div className="flex justify-between items-center">
            <h2 className="font-extrabold text-brand-green-deep">🎉 Recent Signups</h2>
            <span className="text-xs font-bold text-gray-500">LAST 7 DAYS · {recentSignups.length}</span>
          </div>
          <ul className="mt-3 divide-y divide-gray-100">
            {recentSignups.map((c) => (
              <li key={c.id} className="py-2 flex justify-between items-center text-sm">
                <Link to={`/customers/${c.id}`} className="flex items-center gap-2 min-w-0 flex-1 hover:underline">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-green-deep text-white text-xs font-extrabold shrink-0">✓</span>
                  <span className="font-bold truncate">{c.name || '—'}</span>
                </Link>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-gray-500">{fmtDateTime(c.activatedAt)}</span>
                  <SignupMethodPill customer={c} currentInvoice={currentInvoiceByCustomer[c.id]} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card lg:col-span-2">
          <div className="flex justify-between items-center">
            <h2 className="font-extrabold">Recent activity</h2>
            <Link to="/invoices" className="text-xs font-bold text-brand-green-deep hover:underline">View all →</Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-sm text-gray-500 mt-3">No invoices yet. Run a billing cycle to get started.</p>
          ) : (
            <ul className="mt-3 divide-y divide-gray-100">
              {recent.map((r) => (
                <li key={r.id} className="py-2 flex justify-between items-center text-sm">
                  <div className="min-w-0">
                    <div className="font-bold truncate">#{r.invoiceNumber} · {r.customerName}</div>
                    <div className="text-xs text-gray-500">
                      {r.billingPeriod || '—'} · {fmtDateTime(r.paidAt || r.createdAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-bold">{fmtMoney(r.amount)}</span>
                    <span className={`${statusPillClass(r.status)}`}>{r.status}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <h2 className="font-extrabold">Quick actions</h2>
          <div className="mt-3 space-y-2">
            <Link to="/customers/new" className="btn-primary w-full">+ Add Customer</Link>
            <Link to="/billing" className="btn-gold w-full">💳 Run Billing Cycle</Link>
            <Link to="/invoices" className="btn-secondary w-full">View Invoices</Link>
          </div>
        </div>
      </div>

      {/* Email activity — last 50 sends written to /emailLog by Cloud
          Functions. Every Resend call (invoice, reminder, receipt, signup,
          test) shows up here so Mark can confirm at a glance what went out
          and when, plus see any failures immediately. */}
      <div className="card">
        <div className="flex justify-between items-center">
          <h2 className="font-extrabold">Email Activity</h2>
          <span className="text-xs font-bold text-gray-500">
            LAST {Math.min(50, emailLog.length)} SENDS
          </span>
        </div>
        {emailLog.length === 0 ? (
          <p className="text-sm text-gray-500 mt-3">
            No email activity yet. The log fills up as Cloud Functions send invoices, reminders, and receipts.
          </p>
        ) : (
          <div className="mt-3 -mx-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <th className="py-2 px-3 font-bold">Customer</th>
                  <th className="py-2 px-3 font-bold">Email</th>
                  <th className="py-2 px-3 font-bold">Type</th>
                  <th className="py-2 px-3 font-bold text-right">Amount</th>
                  <th className="py-2 px-3 font-bold text-right">Status</th>
                  <th className="py-2 px-3 font-bold text-right">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {emailLog.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 px-3 font-bold truncate max-w-[160px]">{row.customerName || '—'}</td>
                    <td className="py-2 px-3 text-gray-600 truncate max-w-[200px]">{row.email || '—'}</td>
                    <td className="py-2 px-3 text-xs text-gray-600">{row.type || '—'}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{row.amount != null ? fmtMoney(row.amount) : '—'}</td>
                    <td className="py-2 px-3 text-right">
                      <span
                        className={`text-xs font-extrabold rounded px-2 py-0.5 ${
                          row.status === 'sent'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                        title={row.status === 'failed' ? (row.reason || '') : ''}
                      >
                        {row.status === 'sent' ? 'SENT' : 'FAILED'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-xs text-gray-500 tabular-nums whitespace-nowrap">
                      {fmtDateTime(row.sentAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// Recent-Signups badge — mirrors the rule on the Customers list and the
// CustomerDetail pill:
//   • Manual / Check: always "MANUAL / CHECK".
//   • Otherwise Stripe; until stripePaymentMethodId is on file, render
//     a neutral "STRIPE" pill (no method promise).
//   • Once stripePaymentMethodId is present, render the confirmed
//     method: "AUTO-DRAFT", "BANK ON FILE", or "CARD ON FILE".
// Realtime: parent reads /customers via watchCustomers (Firebase
// onValue), so writes to stripePaymentMethodId re-render this badge
// automatically.
function SignupMethodPill({ customer, currentInvoice }) {
  const m = paymentMethodOf(customer)
  const STYLES = {
    mail:             { label: '✉️ Mail / Check', fg: '#92400E', bg: '#FFFBEB', bd: '#FDE68A' },
    no_method:        { label: 'No Payment Method', fg: '#374151', bg: '#F3F4F6', bd: '#E5E7EB' },
    paid_card:        { label: '✓ Paid (card)',  fg: '#065F46', bg: '#ECFDF5', bd: '#A7F3D0' },
    auto_draft:       { label: '✓ Auto-Draft',   fg: '#065F46', bg: '#ECFDF5', bd: '#A7F3D0' },
    bank_on_file:     { label: '✓ Bank on File', fg: '#1E3A8A', bg: '#EFF6FF', bd: '#BFDBFE' },
    card_on_file:     { label: '✓ Card on File', fg: '#9D174D', bg: '#FDF2F8', bd: '#FBCFE8' },
  }
  let key
  if (m === 'mail') key = 'mail'
  // No saved PM and unpaid → one simple "No Payment Method" label (covers the
  // manual fallback and unfinished stripe setup). A one-time card payment saves
  // no PM but is still paid → "Paid (card)".
  else if (!customer?.stripePaymentMethodId && !isPaidOrSetUp(customer, currentInvoice)) key = 'no_method'
  else if (!customer?.stripePaymentMethodId) key = 'paid_card'
  else if (m === 'stripe_invoice') key = 'bank_on_file'
  else if (m === 'card_auto' || m === 'card_invoice') key = 'card_on_file'
  else key = 'auto_draft'
  const p = STYLES[key]
  return (
    <span
      className="text-xs font-extrabold rounded px-2 py-0.5 border"
      style={{ color: p.fg, backgroundColor: p.bg, borderColor: p.bd }}>
      {p.label.toUpperCase()}
    </span>
  )
}

function Stat({ label, value, hint, tone, accent }) {
  const toneClass = tone === 'red' ? 'text-red-600' : tone === 'green' ? 'text-brand-green-deep' : ''
  return (
    <div className={`card ${accent ? 'bg-brand-green text-white border-transparent' : ''}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? 'text-brand-gold' : 'text-gray-500'}`}>{label}</div>
      <div className={`text-2xl font-extrabold mt-1 ${toneClass} ${accent ? 'text-white' : ''}`}>{value}</div>
      {hint ? <div className={`text-xs mt-0.5 ${accent ? 'text-white/70' : 'text-gray-500'}`}>{hint}</div> : null}
    </div>
  )
}
